import { Transcript } from '@/types/note';
import { useState, useEffect } from 'react';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import { FIREBASE_STORAGE_ERRORS } from '@/helpers/errors';
import { logger } from '@/services/loggerService';
import { normalizeTranscriptEntries } from '@/utils/transcript';

type UseTranscriptResult = {
  transcriptList: Transcript[] | null;
  isLoading: boolean;
  error: string | null;
};

const useFetchTranscript = (transcriptUrl: string | null): UseTranscriptResult => {
  const [transcriptList, setTranscriptList] = useState<Transcript[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!transcriptUrl) {
      logger.debug('notes', 'Skipping transcript fetch because transcript URL is missing');
      setTranscriptList(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Stale-response guard: the Note page stays mounted across note navigation,
    // so an in-flight fetch for the previous transcript must not overwrite state.
    let isActive = true;

    const fetchTranscript = async () => {
      setIsLoading(true);
      setError(null);
      logger.debug('notes', 'Fetching transcript JSON', { transcriptUrl });

      try {
        const secureUrl = await resolveMediaUrl(transcriptUrl);
        if (!secureUrl) return;

        const response = await fetch(secureUrl);
        if (!isActive) return;
        if (response.status === 403) {
          // Signed CDN URLs expire ~24h after the API served them.
          // Correct recovery is a note refetch, not a client-side re-sign.
          setError('This transcript link has expired. Please refresh the page to try again.');
          setTranscriptList(null);
          return;
        }
        if (!response.ok) {
          throw new Error(`Error while fetching the transcript: ${response.statusText}`);
        }
        const data = await response.json();
        if (!isActive) return;
        if (!Array.isArray(data)) {
          logger.warn('notes', 'Transcript payload is not an array', {
            transcriptUrl,
            payloadType: typeof data,
            payloadKeys: data && typeof data === 'object' ? Object.keys(data as Record<string, unknown>) : [],
          });
          setError('Transcript payload format is invalid.');
          setTranscriptList(null);
          return;
        }

        logger.debug('notes', 'Transcript JSON loaded', {
          transcriptUrl,
          entries: data.length,
        });
        setTranscriptList(normalizeTranscriptEntries(data));
      } catch (err: unknown) {
        if (!isActive) return;
        const firebaseErr = err as { code?: string; message?: string };
        const errorCode = firebaseErr?.code || '';

        if (
          errorCode === FIREBASE_STORAGE_ERRORS.UNAUTHORIZED ||
          errorCode === FIREBASE_STORAGE_ERRORS.UNAUTHENTICATED
        ) {
          setError(
            'You do not have permission to view this transcript. Please make sure you are logged in with the correct account that has access to this content.',
          );
        } else if (errorCode === FIREBASE_STORAGE_ERRORS.OBJECT_NOT_FOUND) {
          setError('The transcript file could not be found. It may have been deleted or moved.');
        } else {
          setError(firebaseErr?.message || String(err) || 'Error fetching transcript');
        }
        logger.warn('notes', 'Transcript fetch failed', {
          transcriptUrl,
          code: errorCode || undefined,
          message: firebaseErr?.message || String(err),
        });
        setTranscriptList(null);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchTranscript();

    return () => {
      isActive = false;
    };
  }, [transcriptUrl]);

  return { transcriptList, isLoading, error };
};

export default useFetchTranscript;
